import { QuestionField } from './QuestionField';
import React, { useCallback } from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Plus, Trash2, GripVertical, Info, ArrowUp, ArrowDown } from 'lucide-react';
import { nanoid } from 'nanoid';

interface ReorderItem {
  id: string;
  content: string;
}

interface ReorderEditorProps {
  items: ReorderItem[];
  correctOrder: string[];
  onChange: (value: { items: ReorderItem[]; correctOrder: string[] }) => void;
}

/** The correct order is the order the items appear in the editor. */
export function ReorderEditor({
  items,
  correctOrder,
  onChange,
}: ReorderEditorProps) {
  const addItem = useCallback(() => {
    const id = nanoid(6);
    const newItem: ReorderItem = { id, content: '' };
    const newItems = [...items, newItem];
    onChange({ items: newItems, correctOrder: [...correctOrder, id] });
  }, [items, correctOrder, onChange]);

  const removeItem = useCallback(
    (itemId: string) => {
      if (items.length <= 2) return; // Min 2 items
      onChange({ items: items.filter((item) => item.id !== itemId), correctOrder: correctOrder.filter((id) => id !== itemId) });
    },
    [items, correctOrder, onChange],
  );

  const updateItemContent = useCallback(
    (itemId: string, content: string) => {
      onChange({ items: items.map((item) => (item.id === itemId ? { ...item, content } : item)), correctOrder });
    },
    [items, correctOrder, onChange],
  );

  const moveItem = useCallback(
    (itemId: string, direction: 'up' | 'down') => {
      const currentIndex = correctOrder.indexOf(itemId);
      if (currentIndex === -1) return;

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= correctOrder.length) return;

      const newOrder = [...correctOrder];
      [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
      onChange({ items, correctOrder: newOrder });
    },
    [items, correctOrder, onChange],
  );

  // Get items in correct order for display
  const orderedItems = correctOrder
    .map((id) => items.find((item) => item.id === id))
    .filter(Boolean) as ReorderItem[];

  return (
    <div className="reorder-editor space-y-4">
      <div className="rounded-lg border border-border bg-muted/40 p-3">
        <div className="flex items-start gap-2">
          <Info size={16} className="mt-0.5 text-muted-foreground" />
          <div className="text-sm text-foreground">
            <p className="font-medium">Creating a reorder question:</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Add items below in their CORRECT order. Students will see them shuffled
              and must drag them back to this order.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Label>Items in Correct Order ({items.length})</Label>
        <QuestionField name="reorderItems"><Button type="button" variant="outline" size="sm" onClick={addItem}>
          <Plus size={14} className="mr-1" />
          Add Item
        </Button></QuestionField>
      </div>

      {items.length < 2 && (
        <p className="text-center text-sm text-amber-600">
          Add at least 2 items for a reorder question.
        </p>
      )}

      <QuestionField name="correctOrder"><div className="space-y-2">
        {orderedItems.map((item, index) => (
          <div
            key={item.id}
            className="flex items-center gap-2 rounded-lg border bg-white p-3"
          >
            <div className="flex flex-col">
              <button
                type="button"
                onClick={() => moveItem(item.id, 'up')}
                disabled={index === 0}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
              >
                <ArrowUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => moveItem(item.id, 'down')}
                disabled={index === orderedItems.length - 1}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
              >
                <ArrowDown size={14} />
              </button>
            </div>
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-sm font-medium text-gray-600">
              {index + 1}
            </div>
            <GripVertical size={16} className="text-gray-300" />
            <QuestionField name={`reorderItems.${items.indexOf(item)}.content`} className="flex-1"><Input
              value={item.content}
              onChange={(e) => updateItemContent(item.id, e.target.value)}
              placeholder={`Item ${index + 1}...`}
              className="flex-1"
            /></QuestionField>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeItem(item.id)}
              disabled={items.length <= 2}
              className="text-gray-400 hover:text-red-500 disabled:opacity-30"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
      </div></QuestionField>

      {items.length >= 2 && (
        <p className="text-center text-xs text-gray-500">
          Students will see these items in a random order and must reorder them correctly.
        </p>
      )}
    </div>
  );
}

export default ReorderEditor;
